import {createTheme, ThemeOptions} from "@mui/material/styles";
import {PaletteOptions} from "@mui/material";
import type {AppSettings} from "../settings/appSettings";

type ThemeVars = {
    primary: string;
    secondary: string;
    backgroundFrom: string;
    backgroundTo: string;
};

const hexToRgb = (hex: string) => {
    const normalized = hex.replace("#", "").trim();
    const full = normalized.length === 3 ? normalized.split("").map((c) => c + c).join("") : normalized;
    const num = parseInt(full, 16);
    return {r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255};
};

const rgba = (hex: string, alpha: number) => {
    const {r, g, b} = hexToRgb(hex);
    return `rgba(${r},${g},${b},${alpha})`;
};

const basePalette = (primary: string, secondary: string): PaletteOptions => ({
    primary: {main: primary},
    secondary: {main: secondary},
    error: {main: "#ff6b6b"},
    warning: {main: "#f6c344"},
    success: {main: "#7be26a"},
    mode: "dark",
});

const typographyBase = {
    fontFamily: "'Roboto', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    fontWeightRegular: 500,
    fontWeightBold: 700,
    fontVariantNumeric: "tabular-nums" as const,
};

const shape = {borderRadius: 14};
const shadows = Array(25).fill("none") as any;
shadows[1] = "0 6px 20px rgba(0,0,0,0.25)";
shadows[2] = "0 10px 35px rgba(0,0,0,0.35)";

const buildGlobalBackground = ({primary, secondary, backgroundFrom, backgroundTo}: ThemeVars) =>
    [
        `radial-gradient(circle at 20% 20%, ${rgba(primary, 0.2)}, transparent 25%)`,
        `radial-gradient(circle at 80% 10%, ${rgba(secondary, 0.25)}, transparent 28%)`,
        "radial-gradient(circle at 60% 70%, rgba(255, 255, 255, 0.05), transparent 40%)",
        `linear-gradient(135deg, ${backgroundFrom}, ${backgroundTo})`,
    ].join(", ");

const componentsOverrides = (vars: ThemeVars): ThemeOptions["components"] => ({
    MuiCssBaseline: {
        styleOverrides: {
            body: {
                background: buildGlobalBackground(vars),
                color: "#f5f7ff",
            },
            ".app-shell": {
                background: "transparent",
            }
        }
    },
    MuiAppBar: {
        styleOverrides: {
            root: {
                background: "rgba(12,18,36,0.9)",
                backdropFilter: "blur(14px)",
                boxShadow: "0 8px 30px rgba(0,0,0,0.35)",
            }
        }
    },
    MuiToolbar: {
        styleOverrides: {
            root: {
                minHeight: 64,
                gap: 12,
            }
        }
    },
    MuiPaper: {
        styleOverrides: {
            root: {
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.06)",
                boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
            }
        }
    },
    MuiMenu: {
        styleOverrides: {
            paper: {
                background: "#0f1730",
                backgroundImage: "none",
            }
        }
    },
    MuiPopover: {
        styleOverrides: {
            paper: {
                background: "#0f1730",
                backgroundImage: "none",
            }
        }
    },
    MuiButton: {
        defaultProps: {disableElevation: true},
        styleOverrides: {
            root: {
                borderRadius: shape.borderRadius,
                textTransform: "none",
                fontWeight: 700,
                boxShadow: `0 12px 30px ${rgba(vars.primary, 0.35)}`,
                "&:hover": {boxShadow: `0 16px 40px ${rgba(vars.primary, 0.45)}`},
                "&:active": {transform: "translateY(1px)", boxShadow: `0 10px 25px ${rgba(vars.primary, 0.35)}`},
            },
            outlined: {
                borderColor: "rgba(255,255,255,0.12)",
                "&:hover": {borderColor: "rgba(255,255,255,0.25)"},
            }
        }
    },
    MuiChip: {
        styleOverrides: {
            root: {
                borderRadius: shape.borderRadius,
                fontWeight: 700,
                letterSpacing: 0.2,
            }
        }
    },
    MuiDialog: {
        styleOverrides: {
            paper: {
                background: "rgba(12,18,36,0.95)",
                border: "1px solid rgba(255,255,255,0.08)",
                boxShadow: "0 30px 80px rgba(0,0,0,0.55)",
            }
        }
    },
    MuiTypography: {
        styleOverrides: {
            root: {
                color: "#f5f7ff",
            }
        }
    }
});

const resolveThemeVars = (settings?: Pick<AppSettings, "theme">): ThemeVars => {
    return {
        primary: settings?.theme?.primary ?? "#24b4ff",
        secondary: settings?.theme?.secondary ?? "#c39bff",
        backgroundFrom: settings?.theme?.backgroundFrom ?? "#090d1c",
        backgroundTo: settings?.theme?.backgroundTo ?? "#0b1021",
    };
};

export const createAdminTheme = (settings?: Pick<AppSettings, "theme">) => {
    const vars = resolveThemeVars(settings);
    return createTheme({
        palette: {
            ...basePalette(vars.primary, vars.secondary),
            background: {
                default: vars.backgroundTo,
                paper: "rgba(255,255,255,0.04)"
            },
        },
        typography: {
            ...typographyBase,
            h1: {fontSize: "2.6rem", fontWeight: 800},
            h2: {fontSize: "2.2rem", fontWeight: 800},
            h3: {fontSize: "1.8rem", fontWeight: 700},
        },
        shape,
        shadows,
        components: componentsOverrides(vars),
    });
};

export const createTvTheme = (settings?: Pick<AppSettings, "theme">) => {
    const vars = resolveThemeVars(settings);
    return createTheme({
        palette: {
            ...basePalette(vars.primary, vars.secondary),
            mode: "dark",
            background: {
                default: vars.backgroundTo,
                paper: "rgba(255,255,255,0.03)"
            },
        },
        typography: {
            ...typographyBase,
            h1: {fontSize: "5rem", fontWeight: 900, letterSpacing: "-0.02em"},
            h2: {fontSize: "3.5rem", fontWeight: 800},
            h3: {fontSize: "2.5rem", fontWeight: 800},
            body1: {fontSize: "1.4rem"},
            button: {fontSize: "1.1rem", fontWeight: 800},
        },
        shape,
        shadows,
        components: componentsOverrides(vars),
    });
};
